import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { Button } from '@antimatterhq/ui/components/button';
import { FormError, FormInput } from '@/components/form/form';
import { CustomFunctionSchemaValues } from '@/zod/schemas/dataSource/Common';
import { useCreateNotebookFunction } from '@/hooks/dbui/useCreateNotebookFunction';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import {
  generateNotebookFunctionName,
  generateNotebookName,
  setCustomFunctionRequest,
} from '@/lib/datasource';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import { FormMultiValues } from '@/components/form/multiValues/FormMultiValues';
import { useGetNotebookLink } from '@/hooks/dbui/useGetNotebookLink';
import { ExternalLink } from '@/components/link/external/ExternalLink';
import { ConfirmationDialog } from '@/components/dialog';

export const CustomNotebookFunction = ({
  values,
  currentTable,
  prefix = '',
  notebookPath = '',
}: {
  values: CustomFunctionSchemaValues & {
    name?: string;
  };
  currentTable: string;
  prefix?: string;
  notebookPath?: string;
}) => {
  const { handleChange, handleBlur, setFieldValue, isSubmitting } =
    useFormikContext<CustomFunctionSchemaValues>();
  const { dataSource } = useConfigDataSource();
  const { data: { url: notebookLink } = {} } = useGetNotebookLink(notebookPath);
  const [
    showOverwriteNotebookFunctionDialog,
    setShowOverwriteNotebookFunctionDialog,
  ] = useState(false);

  const {
    mutateAsync: mutateCustomNotebook,
    isPending: isCreatingCustomNotebook,
  } = useCreateNotebook();
  const {
    mutateAsync: createNotebookFunction,
    isPending: isCreatingNotebookFunction,
  } = useCreateNotebookFunction();

  const functionName = `${prefix}customFunction`;
  const optionsName = `${prefix}customFunctionOptions`;

  const createCustomNotebookFunction = useCallback(async () => {
    setShowOverwriteNotebookFunctionDialog(false);
    let customNotebookPath = notebookPath;
    if (!customNotebookPath) {
      const path = generateNotebookName(dataSource.metadata?.name || '');
      await mutateCustomNotebook(
        { path, templateType: 'datasource' },
        {
          onSuccess: async ({ data }) => {
            customNotebookPath = data.notebookPath;
            setFieldValue('customNotebookPath', customNotebookPath);
          },
          toasts: {
            success: 'Notebook created!',
            error: 'Failed to create notebook',
          },
        },
      );
    }
    const name = generateNotebookFunctionName(
      dataSource.metadata?.name || '',
      currentTable,
    );
    const request = setCustomFunctionRequest(customNotebookPath || '', name);
    createNotebookFunction(request, {
      onSuccess: async ({ data }) => {
        setFieldValue(functionName, name);
        window.open(data.notebookUrl, '_blank', 'noopener,noreferrer');
      },
      toasts: {
        success: 'Notebook function created',
        error: 'Failed to create notebook function',
      },
    });
  }, [
    createNotebookFunction,
    dataSource,
    setFieldValue,
    currentTable,
    functionName,
    mutateCustomNotebook,
    notebookPath,
  ]);

  const onCreateCustomNotebookFunction = useCallback(async () => {
    if (values.customFunction) {
      setShowOverwriteNotebookFunctionDialog(true);
      return;
    }
    createCustomNotebookFunction();
  }, [values.customFunction, createCustomNotebookFunction]);

  return (
    <>
      <Label htmlFor={functionName} id={`${functionName}-label`} required>
        Function name
      </Label>

      <div className="flex items-center gap-2">
        <FormInput
          name={functionName}
          value={values.customFunction ?? undefined}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Name"
          aria-labelledby={`${functionName}-label`}
        />
        {!!notebookLink && (
          <ExternalLink
            url={notebookLink}
            data-testid={`${prefix}-external-link`}
          />
        )}
      </div>
      <FormError name={functionName} />

      <Spacer size="lg" />

      <Label>Parameters</Label>
      <FormMultiValues
        name={optionsName}
        onBlur={handleBlur}
        onChange={handleChange}
        values={values.customFunctionOptions}
        defaultValue={{ key: '', value: '' }}
      />

      <Spacer size="lg" />

      <div>
        <Button
          variant="default"
          onClick={() => onCreateCustomNotebookFunction()}
          disabled={
            isSubmitting ||
            !values.name ||
            isCreatingNotebookFunction ||
            isCreatingCustomNotebook
          }
          leftIcon="lucide:notebook"
          loading={isCreatingNotebookFunction || isCreatingCustomNotebook}
          data-testid="create-custom-function-button"
        >
          Create a custom function
        </Button>
      </div>
      <ConfirmationDialog
        open={showOverwriteNotebookFunctionDialog}
        title="Overwrite notebook function name"
        body="This will overwrite the existing notebook function name, are you sure?"
        onConfirm={() => createCustomNotebookFunction()}
        onCancel={() => setShowOverwriteNotebookFunctionDialog(false)}
      />
    </>
  );
};
